import { cn } from '@repo/ui'
import type { KeyboardEvent, PointerEvent } from 'react'

/**
 * A thin, draggable vertical divider for resizing an adjacent pane.
 *
 * Reports pointer movement as incremental deltas (px since the last move) so the
 * parent owns the width state and clamping; `onCommit` fires once the drag (or a
 * keyboard nudge) settles, which is the moment to persist the chosen width.
 */
export function PaneResizer({
  value,
  min,
  max,
  onDelta,
  onCommit,
  onResizeStart,
  onResizeEnd,
  ariaLabel,
  className
}: {
  value: number
  min: number
  max: number
  onDelta: (deltaPx: number) => void
  onCommit: () => void
  onResizeStart?: () => void
  onResizeEnd?: () => void
  ariaLabel: string
  className?: string
}) {
  function handlePointerDown(e: PointerEvent<HTMLDivElement>) {
    // Only respond to the primary (left) button.
    if (e.button !== 0) return
    e.preventDefault()
    onResizeStart?.()
    let lastX = e.clientX
    const onMove = (ev: globalThis.PointerEvent) => {
      onDelta(ev.clientX - lastX)
      lastX = ev.clientX
    }
    const finish = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', finish)
      window.removeEventListener('pointercancel', finish)
      document.body.style.removeProperty('cursor')
      document.body.style.removeProperty('user-select')
      onCommit()
      onResizeEnd?.()
    }
    // Lock the cursor/selection globally so the drag stays smooth even when the
    // pointer outruns the 2px handle.
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', finish)
    window.addEventListener('pointercancel', finish)
  }

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      onDelta(-16)
      onCommit()
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      onDelta(16)
      onCommit()
    }
  }

  // WAI-ARIA window-splitter pattern: a focusable `separator` with aria-value*
  // and arrow-key support. jsx-a11y does not model focusable separators, so its
  // non-interactive checks are disabled for this one element.
  /* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={Math.round(value)}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onKeyDown={handleKeyDown}
      className={cn(
        'group relative flex w-2 shrink-0 cursor-col-resize touch-none items-stretch justify-center focus-visible:outline-none',
        className
      )}
    >
      <span className="w-px bg-border transition-colors group-hover:bg-primary/50 group-focus-visible:bg-primary" />
    </div>
  )
  /* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */
}
